import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Eye, Check, X, Filter, Calendar, User } from "lucide-react";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface RemoteWorkRequest {
  id: string;
  destination_country: string;
  start_date: string;
  end_date: string;
  requested_points: number;
  status: string;
  comment: string | null;
  created_at: string;
  processed_at: string | null;
  processed_by: string | null;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    position: string | null;
  };
}

interface RequestsTabProps {
  company: Company | null;
}

const RequestsTab = ({ company }: RequestsTabProps) => {
  const [requests, setRequests] = useState<RemoteWorkRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RemoteWorkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState<RemoteWorkRequest | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      fetchRequests();
    }
  }, [company]);

  useEffect(() => {
    let filtered = requests;
    
    if (statusFilter !== "all") {
      filtered = requests.filter(request => request.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
  }, [requests, statusFilter]);

  const fetchRequests = async () => {
    if (!company) return;

    try {
      // Use the secure RPC function that returns filtered view data
      const { data: viewData, error } = await supabase
        .rpc('get_company_remote_work_requests');

      if (error) throw error;

      // Now fetch employee details for each request
      const requestsWithEmployees = await Promise.all(
        (viewData || []).map(async (request) => {
          const { data: empData } = await supabase
            .from('employees')
            .select('id, first_name, last_name, email, position')
            .eq('id', request.employee_id)
            .single();

          return {
            ...request,
            employee: empData || {
              id: request.employee_id,
              first_name: 'N/A',
              last_name: 'N/A',
              email: 'N/A',
              position: null
            }
          };
        })
      );

      // Sort by created_at descending
      requestsWithEmployees.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setRequests(requestsWithEmployees);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les demandes"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRequestStatus = async (requestId: string, newStatus: 'approved' | 'rejected' | 'pending') => {
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) return;

      // Update request status
      const { error } = await supabase
        .from('remote_work_requests')
        .update({
          status: newStatus,
          processed_at: new Date().toISOString(),
          comment: comment || null
        })
        .eq('id', requestId);

      if (error) throw error;

      // If approved, debit points from employee
      if (newStatus === 'approved') {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();

        // Get or create employee points for current month
        const { data: pointsData, error: pointsSelectError } = await supabase
          .from('employee_points')
          .select('*')
          .eq('employee_id', request.employee.id)
          .eq('month', currentMonth)
          .eq('year', currentYear)
          .maybeSingle();

        if (pointsSelectError) throw pointsSelectError;

        if (pointsData) {
          // Update existing points
          const { error: pointsUpdateError } = await supabase
            .from('employee_points')
            .update({
              used_points: pointsData.used_points + request.requested_points
            })
            .eq('id', pointsData.id);

          if (pointsUpdateError) throw pointsUpdateError;
        } else {
          // Create new points record
          const { error: pointsInsertError } = await supabase
            .from('employee_points')
            .insert({
              employee_id: request.employee.id,
              month: currentMonth,
              year: currentYear,
              total_points: 10,
              used_points: request.requested_points
            });

          if (pointsInsertError) throw pointsInsertError;
        }
      }

      toast({
        title: "Demande mise à jour",
        description: `La demande a été ${newStatus === 'approved' ? 'approuvée et les points ont été débités' : newStatus === 'rejected' ? 'refusée' : 'mise à jour'}`
      });

      setIsDetailDialogOpen(false);
      setComment("");
      fetchRequests();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour la demande"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">En attente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approuvée</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Refusée</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const openRequestDetail = (request: RemoteWorkRequest) => {
    setSelectedRequest(request);
    setComment(request.comment || "");
    setIsDetailDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Demandes de télétravail
              </CardTitle>
              <CardDescription>
                Gérez les {requests.length} demandes de télétravail de votre équipe
              </CardDescription>
            </div>
            
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Refusées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {requests.length === 0 ? "Aucune demande pour le moment" : "Aucune demande ne correspond aux filtres"}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employé</TableHead>
                  <TableHead>Pays</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Demandé le</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {request.employee.first_name} {request.employee.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {request.employee.position || 'Non spécifié'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{request.destination_country}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        <div className="text-sm">
                          <div>{new Date(request.start_date).toLocaleDateString('fr-FR')}</div>
                          <div className="text-muted-foreground">au {new Date(request.end_date).toLocaleDateString('fr-FR')}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{request.requested_points}</span>
                        <span className="text-xs text-muted-foreground">points</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                    <TableCell>
                      {new Date(request.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRequestDetail(request)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Détails
                        </Button>
                        
                        {(request.status === 'pending') && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleUpdateRequestStatus(request.id, 'approved')}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleUpdateRequestStatus(request.id, 'rejected')}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Request Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Détails de la demande
            </DialogTitle>
            <DialogDescription>
              Demande de {selectedRequest?.employee.first_name} {selectedRequest?.employee.last_name}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Employé</Label>
                  <p className="text-sm">{selectedRequest.employee.first_name} {selectedRequest.employee.last_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedRequest.employee.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Poste</Label>
                  <p className="text-sm">{selectedRequest.employee.position || 'Non spécifié'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Pays de destination</Label>
                  <p className="text-sm">{selectedRequest.destination_country}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Points demandés</Label>
                  <p className="text-sm">{selectedRequest.requested_points} points</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Date de début</Label>
                  <p className="text-sm">{new Date(selectedRequest.start_date).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Date de fin</Label>
                  <p className="text-sm">{new Date(selectedRequest.end_date).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Statut actuel</Label>
                  <div className="mt-1">
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Demandé le</Label>
                  <p className="text-sm">{new Date(selectedRequest.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              <div>
                <Label htmlFor="comment">Commentaire</Label>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ajouter un commentaire (optionnel)..."
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {(selectedRequest?.status === 'pending') && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => selectedRequest && handleUpdateRequestStatus(selectedRequest.id, 'rejected')}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="h-4 w-4 mr-2" />
                  Refuser
                </Button>
                <Button
                  onClick={() => selectedRequest && handleUpdateRequestStatus(selectedRequest.id, 'approved')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approuver
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestsTab;